import React, { useEffect, useRef, useState } from "react";
import "../assets/users.css";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import DeleteModal from "../components/DeleteModal.jsx/DeleteModal";
import { toast } from "react-toastify";
const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [status, setStatus] = useState({ all: 0, staff: 0, disabled: 0 });
  const [search, setSearch] = useState(""); // What the input shows (updates per keystroke)
  const [debouncedSearch, setDebouncedSearch] = useState(""); // What we actually query on
  const [activeFilter, setActiveFilter] = useState('all'); // Track active filter
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const navigate = useNavigate();
  const { user: currentUser, logout, hasRoleLevel, updateUser } = useAuth();
  const { hasPermission } = usePermissions();

  // Pagination calculations for server-side
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle search with pagination reset. Only the input value changes here -
  // the query is fired from the debounce effect below so that typing does not
  // queue one full round trip per keystroke.
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render pagination controls (matching Laravel DataTables style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5; // Show max 5 page numbers at a time
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust if we're near the beginning or end
    if (currentPage <= halfRange) {
      endPage = Math.min(maxPagesToShow, totalPages);
    }
    if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <li key={1} className={currentPage === 1 ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(1); }}>
            1
          </a>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis-start" className="disabled">
            <span>...</span>
          </li>
        );
      }
    }

    // Add page numbers in range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={currentPage === i ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>
            {i}
          </a>
        </li>
      );
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis-end" className="disabled">
            <span>...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className={currentPage === totalPages ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}>
            {totalPages}
          </a>
        </li>
      );
    }

    return (
      <div className="dataTables_paginate">
        <ul className="pagination">
          <li className={currentPage === 1 ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }}>
              Previous
            </a>
          </li>
          {pages}
          <li className={currentPage === totalPages ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }}>
              Next
            </a>
          </li>
        </ul>
      </div>
    );
  };

  // Helper to fetch status overview
  const fetchStatusOverview = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/users/status-overview`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then(setStatus);
  };

  // Map frontend sort field to backend column index
  const getSortColumnIndex = (field) => {
    const mapping = {
      "id": 0,
      "name": 1,
      "email": 2,
      "role": 3,
      "calendar_color": 4,
      "last_login": 5,
      "created_at": 6
    };
    return mapping[field] || 0;
  };

  // Tracks the latest in-flight request so a slow earlier response can't
  // overwrite the results of a newer one (common while typing in search).
  const requestSeq = useRef(0);

  // Helper to fetch users with server-side DataTables pagination
  const fetchUsers = () => {
    setLoading(true);
    const seq = ++requestSeq.current;

    const datatableParams = {
      draw: draw,
      start: startIndex,
      length: pageSize,
      search: { value: debouncedSearch },
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
      boxStatus: activeFilter
    };

    fetch(`${process.env.REACT_APP_API_URL}/api/users/datatable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(datatableParams)
    })
      .then((res) => res.json())
      .then((data) => {
        if (seq !== requestSeq.current) return; // A newer request has superseded this one
        setUsers(data.data || []);
        setTotalRecords(data.recordsFiltered || 0);
        setDraw(data.draw);
        setLoading(false);
      })
      .catch((error) => {
        if (seq !== requestSeq.current) return;
        console.error("❌ [FRONTEND] Error fetching users:", error);
        setUsers([]);
        setTotalRecords(0);
        setLoading(false);
      });
  };

  // Debounce the search box so a burst of keystrokes results in one query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch the current page when any listing parameter changes
  useEffect(() => {
    fetchUsers();
  }, [activeFilter, currentPage, pageSize, debouncedSearch, sortField, sortDirection]);

  // The status boxes are counts over the whole table and don't depend on the
  // current page, sort or search, so fetch them once on mount. Mutations
  // (delete/restore) refresh them explicitly.
  useEffect(() => {
    fetchStatusOverview();
  }, []);

  const handleShow = (id) => navigate(`/users/${id}`);
  const handleEdit = (id) => navigate(`/users/${id}/edit`);
  const handleCalendar = (id) => navigate(`/property_viewings/user/${id}`);
  
  // Handle status box filtering (matching Laravel behavior)
  const handleStatusFilter = (filter) => {
    console.log("🎯 [FRONTEND] Status filter clicked:", filter);
    setActiveFilter(filter);
  };
  
  // Handle restore user functionality (matching Laravel)
  const handleRestore = (id) => {
    console.log("🔄 [FRONTEND] Restoring user:", id);
    setLoading(true);
    
    fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/restore`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          toast.success("User restored successfully");
          fetchUsers(activeFilter); // Refresh current view
          fetchStatusOverview(); // Update counts
        } else {
          toast.error("Failed to restore user");
        }
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error restoring user:", error);
        toast.error("Failed to restore user");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const handleToggleCanReview = (id, checked) => {
    fetch(
      `${process.env.REACT_APP_API_URL}/api/users/${id}/can-review-certificates`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ can_review_certificates: checked ? 1 : 0 }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === id ? { ...u, can_review_certificates: checked ? 1 : 0 } : u
            )
          );
          // If the current user toggled their own access, update auth state so the
          // "Certificate Review Queue" menu shows/hides live (no re-login needed).
          if (currentUser && currentUser.id === id) {
            updateUser({ can_review_certificates: checked ? 1 : 0 });
          }
          toast.success("Certificate-review access updated");
        } else {
          toast.error(data.message || "Failed to update");
        }
      })
      .catch(() =>
        toast.error("Failed to update certificate-review access")
      );
  };

  const handleTogglePliReview = (id, checked) => {
    fetch(
      `${process.env.REACT_APP_API_URL}/api/users/${id}/can-review-pli`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ can_review_pli: checked ? 1 : 0 }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === id ? { ...u, can_review_pli: checked ? 1 : 0 } : u
            )
          );
          if (currentUser && currentUser.id === id) {
            updateUser({ can_review_pli: checked ? 1 : 0 });
          }
          toast.success("PLI-review access updated");
        } else {
          toast.error(data.message || "Failed to update");
        }
      })
      .catch(() => toast.error("Failed to update PLI-review access"));
  };

  // P2P v1 — access to "New bills from Xero" and "MyGlobe vs Xero".
  // Same model as the two toggles above: off by default, granted per staff user.
  const handleToggleXeroBills = (id, checked) => {
    fetch(
      `${process.env.REACT_APP_API_URL}/api/users/${id}/can-manage-xero-bills`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ can_manage_xero_bills: checked ? 1 : 0 }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === id ? { ...u, can_manage_xero_bills: checked ? 1 : 0 } : u
            )
          );
          // Refresh the current user so the menu updates without a re-login.
          if (currentUser && currentUser.id === id) {
            updateUser({ can_manage_xero_bills: checked ? 1 : 0 });
          }
          toast.success("Xero-bills access updated");
        } else {
          toast.error(data.message || "Failed to update");
        }
      })
      .catch(() => toast.error("Failed to update Xero-bills access"));
  };

  const handleRemove = (id) => {
    setLoading(true);
    // if (window.confirm("Are you sure you want to remove this user?")) {
    fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.requiresConfirmation) {
          // Show confirmation dialog with related records info

          // User confirmed, make the confirmed deletion request
          fetch(`${process.env.REACT_APP_API_URL}${data.confirmUrl}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
            .then((res) => res.json())
            .then((deleteData) => {
              if (deleteData.message) {
                fetchUsers(); // Refresh with server-side data
                fetchStatusOverview();
                setLoading(false);

                // show toast

                // Check if the deleted user is the current user
                if (currentUser && currentUser.id === id) {
                  // Logout the current user and redirect to login
                  logout();
                  navigate("/login");
                }
              } else {
                setLoading(false);
                alert("Failed to delete user");
              }
            })
            .catch((error) => {
              setLoading(false);
              console.error("Error deleting user:", error);
              toast.error("Failed to delete user");
            });
        } else if (data.message) {
          fetchUsers(); // Refresh with server-side data
          fetchStatusOverview();
          toast.success("User deleted successfully");
          setIsOpenDeleteModal(false);
          setLoading(false);

          // Check if the deleted user is the current user
          if (currentUser && currentUser.id === id) {
            // Logout the current user and redirect to login
            logout();
            navigate("/login");
          }
        } else {
          alert("Failed to delete user");
        }
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        alert("Failed to delete user");
      });
    // }
  };

  return (
    <div className="container-fluid users-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Users</span>
        </div>
      </div>

      <DeleteModal
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        onConfirm={() => handleRemove(userId)}
        isLoading={loading}
      />

      {/* Users Overview */}
      <div className="users-overview-container">
        <div className="users-overview-title">Users overview</div>

        <div className="users-overview-row">
          <div 
            className={`overview-card all-active ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('all')}
            style={{ cursor: 'pointer' }}
          >
            <div className="overview-value">{status.all}</div>
            <div className="overview-label text-white">All active</div>
          </div>
          <div 
            className={`overview-card staff ${activeFilter === 'staff' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('staff')}
            style={{ cursor: 'pointer' }}
          >
            <div className="overview-value">{status.staff}</div>
            <div className="overview-label text-white">Staff</div>
          </div>
          <div 
            className={`overview-card disabled ${activeFilter === 'disabled' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('disabled')}
            style={{ cursor: 'pointer' }}
          >
            <div className="overview-value">{status.disabled}</div>
            <div className="overview-label text-white">Disabled</div>
          </div>
        </div>
      </div>

      {/* Users Table Panel */}
      <div className="users-table-panel">
        <div className="users-table-header-row">
          <div className="users-table-title">Users</div>
          {hasPermission('users.create') && (
            <button
              className="btn btn-add-user"
              onClick={() => navigate("/users/create")}
            >
              <FaPlus style={{ marginRight: 8 }} /> Add user
            </button>
          )}
        </div>
        <div className="users-table-toolbar">
          <div className="users-table-entries">
            Show
            <select
              className="entries-select"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </div>
          <div className="users-table-search">
            Search:
            <input
              type="text"
              className="form-control bg-white"
              placeholder=""
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="users-table-wrapper" style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
              fontSize: '16px',
              color: '#666'
            }}>
              Loading...
            </div>
          )}
          <table className="users-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => handleSort("id")}
                  style={{ cursor: "pointer" }}
                >
                  #<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  E-mail<SortIcon direction={sortField === "email" ? sortDirection : null} isActive={sortField === "email"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("role")}
                  style={{ cursor: "pointer" }}
                >
                  Role<SortIcon direction={sortField === "role" ? sortDirection : null} isActive={sortField === "role"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("calendar_color")}
                  style={{ cursor: "pointer" }}
                >
                  Calendar Color<SortIcon direction={sortField === "calendar_color" ? sortDirection : null} isActive={sortField === "calendar_color"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("last_login")}
                  style={{ cursor: "pointer" }}
                >
                  Last login<SortIcon direction={sortField === "last_login" ? sortDirection : null} isActive={sortField === "last_login"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("created_at")}
                  style={{ cursor: "pointer" }}
                >
                  Created at<SortIcon direction={sortField === "created_at" ? sortDirection : null} isActive={sortField === "created_at"} />
                </th>
                <th className="text-center">Cert Review</th>
                <th className="text-center">PLI Review</th>
                <th className="text-center">Xero Bills</th>
                <th className="action-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {activeFilter === 'disabled'
                      ? 'No disabled users found'
                      : activeFilter === 'staff'
                      ? 'No staff users found'
                      : 'No users found'
                    }
                  </td>
                </tr>
              ) : (
                users.map((u, idx) => (
                <tr key={u.id || idx}>
                  <td>{startIndex + idx + 1}</td>
                  <td>
                    <button
                      className="text-primary user-link btn-link"
                      onClick={() => handleEdit(u.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textDecoration: 'underline',
                        color: 'inherit',
                        fontWeight: 700
                      }}
                    >
                      {`${u.first_name || ""} ${
                        u.last_name || ""
                      }`.trim()}
                    </button>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.roleName || "-"}</td>
                  <td>
                    {u.color ? (
                      <span
                        className="label"
                        style={{
                          backgroundColor: u.color,
                          color: "#fff",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.85em",
                        }}
                      >
                        {u.color}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{u.last_login || "-"}</td>
                  <td>{u.created_at || "-"}</td>
                  <td className="text-center">
                    {u.staff ? (
                      <input
                        type="checkbox"
                        checked={!!u.can_review_certificates}
                        onChange={(e) =>
                          handleToggleCanReview(u.id, e.target.checked)
                        }
                        disabled={!hasPermission("users.edit")}
                        title="Can review certificates"
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center">
                    {u.staff ? (
                      <input
                        type="checkbox"
                        checked={!!u.can_review_pli}
                        onChange={(e) =>
                          handleTogglePliReview(u.id, e.target.checked)
                        }
                        disabled={!hasPermission("users.edit")}
                        title="Can review PLI certificates"
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center">
                    {u.staff ? (
                      <input
                        type="checkbox"
                        checked={!!u.can_manage_xero_bills}
                        onChange={(e) =>
                          handleToggleXeroBills(u.id, e.target.checked)
                        }
                        disabled={!hasPermission("users.edit")}
                        title="Can see New bills from Xero and MyGlobe vs Xero"
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center">
                    {u.disabled ? (
                      // Disabled user - show only restore button (matching Laravel @can('users.restore'))
                      hasPermission('users.restore') && (
                        <button
                          className="btn-action restore"
                          data-userid={u.id}
                          onClick={() => handleRestore(u.id)}
                          style={{ backgroundColor: '#5cb85c', color: 'white' }}
                        >
                          Restore
                        </button>
                      )
                    ) : (
                      // Active user - show buttons based on permissions (matching Laravel @can directives)
                      <>
                        {hasPermission('users.show') && (
                          <button
                            className="btn-action show"
                            data-userid={u.id}
                            onClick={() => handleShow(u.id)}
                          >
                            Show
                          </button>
                        )}
                        {hasPermission('users.edit') && (
                          <button
                            className="btn-action edit"
                            data-userid={u.id}
                            onClick={() => handleEdit(u.id)}
                          >
                            Edit
                          </button>
                        )}
                        {hasPermission('users.destroy') && (hasRoleLevel(9) || (hasRoleLevel(8) && currentUser?.email === 'global.guardians@theaimegroup.com')) && (
                          <button
                            className="btn-action remove"
                            data-userid={u.id}
                            onClick={() => {
                              setUserId(u.id);
                              setIsOpenDeleteModal(true);
                            }}
                          >
                            Remove
                          </button>
                        )}
                        {u.staff && hasPermission('property_viewings.show') && (
                          <button
                            className="btn-action calendar"
                            data-userid={u.id}
                            onClick={() => handleCalendar(u.id)}
                          >
                            Viewing Calendar
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Info and Controls */}
        <div className="dataTables_info_wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <div className="dataTables_info">
            Showing {totalRecords === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(startIndex + pageSize, totalRecords)} of {totalRecords} entries
          </div>
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default Users;
